import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Select, Button, Tag, Spin } from "antd";
import { useSnackbar } from "notistack";
import {
  ArrowLeft,
  Calendar,
  Building2,
  FileSpreadsheet,
  CreditCard,
  Landmark,
  Download,
  Printer,
  CheckCircle2,
  Clock,
  Layers,
  HardDrive,
} from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { getOrganizationMonthlyReport } from "../../utils/users";

const { Option } = Select;

// Generate last 12 months for selector
const getLast12Months = () => {
  const months = [];
  const date = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(date.getFullYear(), date.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("default", { month: "long", year: "numeric" });
    months.push({ value, label });
  }
  return months;
};

// Reusable SVG Interactive Area / Line Chart
const InteractiveChart = ({
  title,
  subtitle,
  icon: Icon,
  data = [],
  colorTheme = {
    primary: "#6366f1",
    secondary: "#a855f7",
    gradientId: "indigoGradient",
    lightBg: "bg-indigo-50",
    textClass: "text-indigo-600",
  },
  valueFormatter = (v) => v.toLocaleString(),
  unitLabel = "",
  summaryBadge = null,
}) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const containerRef = useRef(null);

  const width = 640;
  const height = 240;
  const padding = { top: 25, right: 25, bottom: 40, left: 55 };

  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const validData = data && data.length > 0 ? data : [];
  const values = validData.map((d) => Number(d.value) || 0);
  const maxValue = values.length > 0 ? Math.max(...values, 1) : 1;

  // Calculate coordinates
  const points = validData.map((d, index) => {
    const x =
      validData.length === 1
        ? padding.left + innerWidth / 2
        : padding.left + (index / (validData.length - 1)) * innerWidth;
    const y = padding.top + innerHeight - (Math.max(0, Number(d.value) || 0) / maxValue) * innerHeight;
    return { ...d, x, y, index };
  });

  // Construct SVG Path with smooth curves
  let pathD = "";
  let areaD = "";

  if (points.length === 1) {
    pathD = `M ${points[0].x - 10} ${points[0].y} L ${points[0].x + 10} ${points[0].y}`;
    areaD = `M ${points[0].x - 10} ${height - padding.bottom} L ${points[0].x - 10} ${points[0].y} L ${points[0].x + 10} ${points[0].y} L ${points[0].x + 10} ${height - padding.bottom} Z`;
  } else if (points.length > 1) {
    pathD = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      const cx = (p0.x + p1.x) / 2;
      pathD += ` C ${cx} ${p0.y}, ${cx} ${p1.y}, ${p1.x} ${p1.y}`;
    }

    areaD = `${pathD} L ${points[points.length - 1].x} ${height - padding.bottom} L ${points[0].x} ${height - padding.bottom} Z`;
  }

  // 4 horizontal grid steps
  const gridSteps = [0, 0.33, 0.66, 1];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col justify-between">
      {/* Chart Header */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${colorTheme.lightBg} ${colorTheme.textClass} flex items-center justify-center shrink-0`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-gray-900">{title}</h4>
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          </div>
        </div>

        {summaryBadge && (
          <div className="text-right">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-100 text-gray-700">
              {summaryBadge}
            </span>
          </div>
        )}
      </div>

      {/* SVG Canvas / Empty State */}
      {validData.length === 0 ? (
        <div className="h-48 flex flex-col items-center justify-center text-center p-4 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
          <Clock className="w-6 h-6 text-gray-300 mb-2" />
          <p className="text-xs font-medium text-gray-500">No backup records for this month</p>
          <p className="text-[11px] text-gray-400 mt-0.5">Graphs will populate as automated cloud backups execute</p>
        </div>
      ) : (
        <div className="relative w-full" ref={containerRef}>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="w-full h-auto overflow-visible select-none"
            onMouseLeave={() => setHoveredPoint(null)}
          >
            <defs>
              <linearGradient id={colorTheme.gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colorTheme.primary} stopOpacity="0.28" />
                <stop offset="100%" stopColor={colorTheme.secondary} stopOpacity="0.01" />
              </linearGradient>
            </defs>

            {/* Horizontal Grid lines */}
            {gridSteps.map((fraction, idx) => {
              const y = padding.top + innerHeight - fraction * innerHeight;
              const stepValue = Math.round(fraction * maxValue);
              return (
                <g key={idx}>
                  <line
                    x1={padding.left}
                    y1={y}
                    x2={width - padding.right}
                    y2={y}
                    stroke="#f1f5f9"
                    strokeDasharray={idx === 0 ? "none" : "3 3"}
                    strokeWidth="1"
                  />
                  <text
                    x={padding.left - 8}
                    y={y + 3}
                    textAnchor="end"
                    className="text-[10px] fill-gray-400 font-medium"
                  >
                    {stepValue >= 1000 ? `${(stepValue / 1000).toFixed(1)}k` : stepValue}
                  </text>
                </g>
              );
            })}

            {/* Area Fill */}
            {areaD && <path d={areaD} fill={`url(#${colorTheme.gradientId})`} />}

            {/* Gradient Curve Line */}
            {pathD && (
              <path
                d={pathD}
                fill="none"
                stroke={colorTheme.primary}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Data Dots & X-axis labels */}
            {points.map((pt, i) => {
              const isHovered = hoveredPoint?.index === i;
              const showLabel =
                points.length <= 8 ||
                i === 0 ||
                i === points.length - 1 ||
                i % Math.ceil(points.length / 6) === 0;

              return (
                <g key={i}>
                  {/* Invisible hit target for smooth hovering */}
                  <circle
                    cx={pt.x}
                    cy={pt.y}
                    r="12"
                    fill="transparent"
                    className="cursor-pointer"
                    onMouseEnter={() => setHoveredPoint(pt)}
                  />

                  {/* Visible data point dot */}
                  <circle
                    cx={pt.x}
                    cy={pt.y}
                    r={isHovered ? 6 : 3.5}
                    fill={isHovered ? "#ffffff" : colorTheme.primary}
                    stroke={colorTheme.primary}
                    strokeWidth={isHovered ? 3 : 1.5}
                    className="transition-all duration-150 pointer-events-none"
                  />

                  {/* X Axis Label */}
                  {showLabel && (
                    <text
                      x={pt.x}
                      y={height - padding.bottom + 18}
                      textAnchor="middle"
                      className="text-[10px] fill-gray-400 font-medium"
                    >
                      {pt.display_date || pt.date.slice(5)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Interactive Tooltip Card */}
          {hoveredPoint && (
            <div
              className="absolute pointer-events-none z-20 bg-gray-900/95 text-white px-3 py-2 rounded-xl shadow-lg text-xs backdrop-blur-sm transform -translate-x-1/2 -translate-y-full transition-all duration-75"
              style={{
                left: `${(hoveredPoint.x / width) * 100}%`,
                top: `${(hoveredPoint.y / height) * 100 - 8}%`,
              }}
            >
              <div className="font-semibold text-gray-200 text-[11px]">
                {hoveredPoint.date}
              </div>
              <div className="font-bold text-sm text-white mt-0.5 flex items-center gap-1">
                <span>{valueFormatter(hoveredPoint.value)}</span>
                {unitLabel && <span className="text-[10px] text-gray-300 font-normal">{unitLabel}</span>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OrgMonthlyReport = () => {
  const { orgId, platform = "xero" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isLoading, getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();

  const reportContainerRef = useRef(null);

  const monthOptions = getLast12Months();
  const initialMonth = searchParams.get("month") || monthOptions[0]?.value || "";
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    if (!isLoading && orgId) {
      loadReport(selectedMonth);
    }
  }, [isLoading, orgId, platform, selectedMonth]);

  const loadReport = async (month) => {
    try {
      setLoading(true);
      const token = await getIdTokenClaims();
      const res = await getOrganizationMonthlyReport(token.__raw, orgId, platform, month);
      if (res.data.success) {
        setReport(res.data);
      } else {
        enqueueSnackbar(res.data.message || "Failed to load organization report.", {
          variant: "error",
        });
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar("An error occurred while fetching report.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (val) => {
    setSelectedMonth(val);
    setSearchParams({ month: val });
  };

  const handleExportCsv = () => {
    if (!report?.daily_metrics || report.daily_metrics.length === 0) {
      enqueueSnackbar("No daily metrics available to export.", { variant: "info" });
      return;
    }

    const headers = [
      "Date",
      "Size (MB)",
      "Invoices Count",
      "Payments Count",
      "Bank Transactions Count",
    ];

    const rows = report.daily_metrics.map((row) => [
      row.date,
      row.size_mb,
      row.invoices_count,
      row.payments_count,
      row.bank_transactions_count,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    const orgSlug = (report.organization?.name || "org").replace(/[^a-zA-Z0-9]/g, "_");
    link.setAttribute("download", `${orgSlug}_monthly_metrics_${selectedMonth}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    enqueueSnackbar("Metrics exported to CSV successfully!", { variant: "success" });
  };

  const handleDownloadPdf = async () => {
    if (!reportContainerRef.current) return;
    try {
      setExportingPdf(true);
      enqueueSnackbar("Preparing high-quality PDF report...", { variant: "info" });

      const canvas = await html2canvas(reportContainerRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pageHeight = pdf.internal.pageSize.getHeight();

      let heightLeft = pdfHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }

      const orgSlug = (report.organization?.name || "organization").replace(/[^a-zA-Z0-9]/g, "_");
      pdf.save(`WOWzer_${orgSlug}_Report_${selectedMonth}.pdf`);
      enqueueSnackbar("PDF report downloaded!", { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to generate PDF.", { variant: "error" });
    } finally {
      setExportingPdf(false);
    }
  };

  const org = report?.organization || {};
  const summary = report?.summary || {};
  const graphs = report?.graphs || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Top Navigation & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Button
            type="default"
            icon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate("/monthly-report")}
            className="flex items-center gap-1 rounded-xl text-gray-600 hover:text-gray-900"
          >
            Back to Report
          </Button>

          <span className="text-gray-300">/</span>

          <span className="text-sm font-semibold text-gray-700 truncate max-w-xs sm:max-w-md">
            {org.name || "Organization Activity Breakdown"}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Month Selector */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <Select
              value={selectedMonth}
              onChange={handleMonthChange}
              className="w-44"
              dropdownMatchSelectWidth={false}
            >
              {monthOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>

          {/* Export Actions */}
          <Button
            type="default"
            icon={<Download className="w-4 h-4 inline mr-1" />}
            onClick={handleExportCsv}
            disabled={loading || !report?.daily_metrics?.length}
            className="flex items-center"
          >
            Export CSV
          </Button>

          <Button
            type="primary"
            icon={<Printer className="w-4 h-4 inline mr-1" />}
            onClick={handleDownloadPdf}
            loading={exportingPdf}
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-700 border-none flex items-center shadow-sm"
          >
            Download PDF Report
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center">
          <Spin size="large" />
          <p className="text-gray-500 text-sm mt-3 font-medium">Loading organization graphs and metrics...</p>
        </div>
      ) : (
        <div ref={reportContainerRef} id="printable-report" className="space-y-6 bg-white p-6 sm:p-8 rounded-3xl border border-gray-100 shadow-sm">
          {/* Organization Header & Metadata Banner */}
          <div className="bg-gradient-to-br from-gray-900 via-slate-900 to-indigo-950 text-white rounded-2xl p-6 sm:p-7 shadow-lg">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              {/* Org Name & Platform */}
              <div className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center text-white border border-white/20">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                        {org.name || "Organization"}
                      </h1>
                      <Tag
                        className={`rounded-full px-3 py-0.5 font-bold text-xs border ${
                          org.platform === "Xero"
                            ? "bg-sky-500/20 text-sky-300 border-sky-400/30"
                            : "bg-emerald-500/20 text-emerald-300 border-emerald-400/30"
                        }`}
                      >
                        {org.platform}
                      </Tag>
                      {org.subscription_status === 1 ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                          <CheckCircle2 className="w-3 h-3" /> Subscribed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-gray-500/20 text-gray-300 border border-gray-500/30">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-300 mt-1">
                      Automated cloud backup analytics &amp; recovery monitoring for {selectedMonth}
                    </p>
                  </div>
                </div>
              </div>

              {/* Renewal Date & Subscribed Modules Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t lg:border-t-0 lg:border-l border-white/10 pt-4 lg:pt-0 lg:pl-6">
                {/* Renewal Date */}
                <div className="bg-white/5 rounded-xl p-3.5 border border-white/10 backdrop-blur-sm">
                  <div className="text-[11px] font-semibold text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-indigo-400" />
                    Renewal Date
                  </div>
                  <div className="mt-1.5 text-base font-bold text-white">
                    {org.renewal_date_formatted ? (
                      <span className="text-emerald-300">{org.renewal_date_formatted}</span>
                    ) : (
                      <span className="text-gray-400 text-sm">Not set / Trial</span>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-400 block mt-0.5">
                    Next billing renewal cycle
                  </span>
                </div>

                {/* Modules Subscribed */}
                <div className="bg-white/5 rounded-xl p-3.5 border border-white/10 backdrop-blur-sm">
                  <div className="text-[11px] font-semibold text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-purple-400" />
                    Modules Subscribed
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {org.modules_subscribed && org.modules_subscribed.length > 0 ? (
                      org.modules_subscribed.map((mod, idx) => (
                        <span
                          key={idx}
                          className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-white/15 text-white border border-white/10"
                        >
                          {mod}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400">Accounting</span>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-400 block mt-0.5">
                    Protected subscription scopes
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 rounded-2xl bg-indigo-50/50 border border-indigo-100">
              <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                Backup Volume
              </span>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-2xl font-extrabold text-indigo-950">
                  {summary.latest_size_mb ? summary.latest_size_mb.toFixed(1) : 0}
                </span>
                <span className="text-xs text-indigo-600 font-bold">MB</span>
              </div>
              <span className="text-[11px] text-gray-500 block mt-0.5">
                Peak: {summary.max_size_mb ? summary.max_size_mb.toFixed(1) : 0} MB
              </span>
            </div>

            <div className="p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100">
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                Total Invoices
              </span>
              <div className="mt-2 text-2xl font-extrabold text-emerald-950">
                {Number(summary.total_invoices || 0).toLocaleString()}
              </div>
              <span className="text-[11px] text-gray-500 block mt-0.5">
                Archived &amp; indexed
              </span>
            </div>

            <div className="p-4 rounded-2xl bg-amber-50/50 border border-amber-100">
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">
                Total Payments
              </span>
              <div className="mt-2 text-2xl font-extrabold text-amber-950">
                {Number(summary.total_payments || 0).toLocaleString()}
              </div>
              <span className="text-[11px] text-gray-500 block mt-0.5">
                Verified records
              </span>
            </div>

            <div className="p-4 rounded-2xl bg-sky-50/50 border border-sky-100">
              <span className="text-xs font-semibold text-sky-700 uppercase tracking-wider">
                Bank Transactions
              </span>
              <div className="mt-2 text-2xl font-extrabold text-sky-950">
                {Number(summary.total_bank_transactions || 0).toLocaleString()}
              </div>
              <span className="text-[11px] text-gray-500 block mt-0.5">
                Bank feeds &amp; lines
              </span>
            </div>
          </div>

          {/* 4 Interactive Graphs Section */}
          <div className="pt-2">
            <div className="flex items-center justify-between pb-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Performance &amp; Activity Graphs
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Visual daily trend lines across cloud storage volume and high-frequency accounting entities.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-3">
              {/* Graph 1: Date vs Size */}
              <InteractiveChart
                title="Date vs Size Graph"
                subtitle="Daily storage consumption over time"
                icon={HardDrive}
                data={graphs.date_vs_size || []}
                unitLabel="MB"
                valueFormatter={(v) => `${Number(v).toFixed(2)} MB`}
                summaryBadge={`Peak: ${summary.max_size_mb ? summary.max_size_mb.toFixed(1) : 0} MB`}
                colorTheme={{
                  primary: "#6366f1",
                  secondary: "#a855f7",
                  gradientId: "indigoPurpleGradient",
                  lightBg: "bg-indigo-50",
                  textClass: "text-indigo-600",
                }}
              />

              {/* Graph 2: Invoices Graph of Counts vs Date */}
              <InteractiveChart
                title="Invoices Graph (Counts vs Date)"
                subtitle="Invoices backed up per backup snapshot"
                icon={FileSpreadsheet}
                data={graphs.invoices_vs_date || []}
                unitLabel="Invoices"
                valueFormatter={(v) => `${Number(v).toLocaleString()} Invoices`}
                summaryBadge={`Total: ${Number(summary.total_invoices || 0).toLocaleString()}`}
                colorTheme={{
                  primary: "#10b981",
                  secondary: "#14b8a6",
                  gradientId: "emeraldTealGradient",
                  lightBg: "bg-emerald-50",
                  textClass: "text-emerald-600",
                }}
              />

              {/* Graph 3: Payment Graph of Counts vs Date */}
              <InteractiveChart
                title="Payment Graph (Counts vs Date)"
                subtitle="Customer payments and receipts captured"
                icon={CreditCard}
                data={graphs.payments_vs_date || []}
                unitLabel="Payments"
                valueFormatter={(v) => `${Number(v).toLocaleString()} Payments`}
                summaryBadge={`Total: ${Number(summary.total_payments || 0).toLocaleString()}`}
                colorTheme={{
                  primary: "#f59e0b",
                  secondary: "#f97316",
                  gradientId: "amberOrangeGradient",
                  lightBg: "bg-amber-50",
                  textClass: "text-amber-600",
                }}
              />

              {/* Graph 4: BankTransaction Graph of Counts vs Date */}
              <InteractiveChart
                title="BankTransaction Graph (Counts vs Date)"
                subtitle="Bank transactions, feeds & transfers logged"
                icon={Landmark}
                data={graphs.bank_transactions_vs_date || []}
                unitLabel="Bank Txns"
                valueFormatter={(v) => `${Number(v).toLocaleString()} Transactions`}
                summaryBadge={`Total: ${Number(summary.total_bank_transactions || 0).toLocaleString()}`}
                colorTheme={{
                  primary: "#0284c7",
                  secondary: "#06b6d4",
                  gradientId: "skyCyanGradient",
                  lightBg: "bg-sky-50",
                  textClass: "text-sky-600",
                }}
              />
            </div>
          </div>

          {/* Daily Records Activity Table */}
          <div className="pt-4">
            <h4 className="text-base font-bold text-gray-900 mb-3">
              Daily Backup Breakdown Table ({selectedMonth})
            </h4>

            <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50/75 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-5 py-3.5">Backup Date</th>
                    <th className="px-5 py-3.5 text-right">Size (MB)</th>
                    <th className="px-5 py-3.5 text-right">Invoices</th>
                    <th className="px-5 py-3.5 text-right">Payments</th>
                    <th className="px-5 py-3.5 text-right">Bank Transactions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {!report?.daily_metrics || report.daily_metrics.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-5 py-8 text-center text-gray-400 text-xs">
                        No daily backup metrics recorded for this month.
                      </td>
                    </tr>
                  ) : (
                    report.daily_metrics.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-5 py-3 font-semibold text-gray-900">
                          {row.date}
                        </td>
                        <td className="px-5 py-3 text-right font-medium text-indigo-600">
                          {Number(row.size_mb).toFixed(2)} MB
                        </td>
                        <td className="px-5 py-3 text-right font-medium text-emerald-600">
                          {Number(row.invoices_count).toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right font-medium text-amber-600">
                          {Number(row.payments_count).toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right font-medium text-sky-600">
                          {Number(row.bank_transactions_count).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgMonthlyReport;
